import React, { useEffect } from 'react';
import { Calendar, Settings, KeyRound, LogOut, BookOpen, ChevronLeft } from 'lucide-react';
import { useDashboardStore } from '../../store/dashboardStore';
import { signOutAuth } from '../../lib/supabaseAuth';
interface BottomMenuBarProps {
  onLogout?: () => Promise<void>;
  /** true: position:fixed（ブラウザ用）, false: flex（PWA用・fixedが効かないため） */
  useFixedPosition?: boolean;
}

export const BottomMenuBar: React.FC<BottomMenuBarProps> = ({ onLogout, useFixedPosition = false }) => {
  const {
    currentUser,
    setShowPinChangeDialog,
    pinChangeMessage,
    setPinChangeMessage,
    showWorkflowStaffListPanel,
    showDatePanel,
    showDetailPanel,
    showSchedulePanel,
    showSchedulePatientListPanel,
    showSettingsPanel,
    showStaffListPanel,
    showAllPatientListPanel,
    showPatientFormPanel,
    showStaffFormPanel,
    showSettingsDatePanel,
    schedulePanelSource,
    setShowDatePanel,
    setShowWorkflowStaffListPanel,
    setShowDetailPanel,
    setShowSchedulePanel,
    setShowSchedulePatientListPanel,
    setShowSettingsPanel,
    setShowAllPatientListPanel,
    setShowStaffListPanel,
    setShowPatientFormPanel,
    setShowStaffFormPanel,
    setShowSettingsDatePanel,
    setSkipFlowSwitchAnimation,
    setSettingsReturningToRoot,
    setWorkflowRootRevealFromSettings,
    setSettingsRootRevealFromWorkflow,
    setIsNavigatingBack,
    navigateBackToPatientList,
    navigateBackToStaffList,
    navigateBackToSchedulePanel,
    navigateBackToDatePanel,
    navigateBackToWorkflowStaffList,
    showSpeechDictionaryPanel,
    setShowSpeechDictionaryPanel,
    openSpeechDictionaryPanel,
  } = useDashboardStore();
  useEffect(() => {
    if (!pinChangeMessage) return;
    const timer = setTimeout(() => setPinChangeMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [pinChangeMessage, setPinChangeMessage]);

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem('sessionToken');
      if (onLogout) {
        await onLogout();
        return;
      }
      await signOutAuth();
      window.location.assign('/login');
    } catch (e) {
      window.location.assign('/login');
    }
  };

  const handleWorkflowClick = () => {
    const state = useDashboardStore.getState();
    setSettingsReturningToRoot(false);
    setSettingsRootRevealFromWorkflow(false);

    const deepInSettings =
      state.showStaffListPanel ||
      state.showAllPatientListPanel ||
      state.showPatientFormPanel ||
      state.showStaffFormPanel ||
      state.showSettingsDatePanel ||
      (state.showSchedulePanel && state.schedulePanelSource === 'settings') ||
      (state.showSchedulePatientListPanel && state.schedulePanelSource === 'settings');

    if (deepInSettings) {
      setWorkflowRootRevealFromSettings(true);
      setIsNavigatingBack(true);
      const isStaff = currentUser?.role === 'staff';
      if (isStaff) {
        setShowWorkflowStaffListPanel(false);
        setShowDatePanel(true);
      } else {
        if (state.showWorkflowStaffListPanel) {
          useDashboardStore.setState((s) => ({ workflowStaffListRevision: s.workflowStaffListRevision + 1 }));
        } else {
          setShowWorkflowStaffListPanel(true);
        }
        setShowDatePanel(false);
      }
      setShowDetailPanel(false);
      setShowSchedulePanel(false);
      setShowSchedulePatientListPanel(false);
      requestAnimationFrame(() => {
        useDashboardStore.setState({
          showSettingsPanel: false,
          showStaffListPanel: false,
          showAllPatientListPanel: false,
          showPatientFormPanel: false,
          showStaffFormPanel: false,
          showSettingsDatePanel: false,
          showSchedulePanel: false,
          showSchedulePatientListPanel: false,
          showSpeechDictionaryPanel: false,
        });
      });
      return;
    }

    // 辞書のみ（診療オーバーレイなし）: 先に辞書を閉じ、次フレームで診療を開く（PC IconSidebar と同型の残像対策）
    const workflowPanelsOpen =
      state.showWorkflowStaffListPanel ||
      state.showDatePanel ||
      state.showDetailPanel ||
      (state.showSchedulePanel && state.schedulePanelSource === 'workflow') ||
      (state.showSchedulePatientListPanel && state.schedulePanelSource === 'workflow');
    if (state.showSpeechDictionaryPanel && !workflowPanelsOpen && !deepInSettings) {
      useDashboardStore.setState({ showSpeechDictionaryPanel: false });
      requestAnimationFrame(() => {
        const st = useDashboardStore.getState();
        const isStaff = currentUser?.role === 'staff';
        if (isStaff) {
          setShowWorkflowStaffListPanel(false);
          setShowDatePanel(true);
        } else {
          if (st.showWorkflowStaffListPanel) {
            useDashboardStore.setState((s) => ({ workflowStaffListRevision: s.workflowStaffListRevision + 1 }));
          } else {
            setShowWorkflowStaffListPanel(true);
          }
          setShowDatePanel(false);
        }
        setShowDetailPanel(false);
        setShowSchedulePanel(false);
        setShowSchedulePatientListPanel(false);
        setShowSettingsPanel(false);
        setShowStaffListPanel(false);
        setShowAllPatientListPanel(false);
        setShowPatientFormPanel(false);
        setShowStaffFormPanel(false);
        setShowSettingsDatePanel(false);
        useDashboardStore.setState({ showSpeechDictionaryPanel: false });
      });
      return;
    }

    setWorkflowRootRevealFromSettings(false);
    const isSettingsActive =
      state.showSettingsPanel ||
      state.showStaffListPanel ||
      state.showAllPatientListPanel ||
      state.showPatientFormPanel ||
      state.showStaffFormPanel ||
      state.showSettingsDatePanel;
    if (isSettingsActive) {
      setSkipFlowSwitchAnimation(true);
    }
    const isStaff = currentUser?.role === 'staff';
    if (isStaff) {
      setShowWorkflowStaffListPanel(false);
      setShowDatePanel(true);
    } else {
      if (state.showWorkflowStaffListPanel) {
        useDashboardStore.setState((s) => ({ workflowStaffListRevision: s.workflowStaffListRevision + 1 }));
      } else {
        setShowWorkflowStaffListPanel(true);
      }
      setShowDatePanel(false);
    }
    setShowDetailPanel(false);
    setShowSchedulePanel(false);
    setShowSchedulePatientListPanel(false);
    setShowSettingsPanel(false);
    setShowStaffListPanel(false);
    setShowAllPatientListPanel(false);
    setShowPatientFormPanel(false);
    setShowStaffFormPanel(false);
    setShowSettingsDatePanel(false);
    useDashboardStore.setState({ showSpeechDictionaryPanel: false });
  };

  const handleSettingsClick = () => {
    const state = useDashboardStore.getState();
    setWorkflowRootRevealFromSettings(false);
    const isWorkflowActive =
      state.showWorkflowStaffListPanel ||
      state.showDatePanel ||
      state.showDetailPanel ||
      (state.showSchedulePanel && state.schedulePanelSource === 'workflow') ||
      (state.showSchedulePatientListPanel && state.schedulePanelSource === 'workflow') ||
      state.showSpeechDictionaryPanel;
    const deepInSettings =
      state.showStaffListPanel ||
      state.showAllPatientListPanel ||
      state.showPatientFormPanel ||
      state.showStaffFormPanel ||
      state.showSettingsDatePanel ||
      (state.showSchedulePanel && state.schedulePanelSource === 'settings') ||
      (state.showSchedulePatientListPanel && state.schedulePanelSource === 'settings');

    if (isWorkflowActive) {
      // 診療オーバーレイを右へ退場させ、背面に設定トップを即表示（settingsReturningToRoot と同型）
      setSettingsRootRevealFromWorkflow(true);
      setIsNavigatingBack(true);
    } else {
      setSettingsRootRevealFromWorkflow(false);
    }
    if (deepInSettings) {
      setSettingsReturningToRoot(true);
      setIsNavigatingBack(true);
    } else {
      setSettingsReturningToRoot(false);
    }
    setShowDatePanel(false);
    setShowWorkflowStaffListPanel(false);
    setShowDetailPanel(false);
    setShowSchedulePanel(false);
    setShowSchedulePatientListPanel(false);
    setShowAllPatientListPanel(false);
    setShowStaffListPanel(false);
    setShowPatientFormPanel(false);
    setShowStaffFormPanel(false);
    setShowSettingsDatePanel(false);
    useDashboardStore.setState({ showSpeechDictionaryPanel: false });
    setShowSettingsPanel(true);
  };

  const handleDictionaryClick = () => {
    openSpeechDictionaryPanel();
  };

  const handleChangePin = () => {
    if (!currentUser?.id) {
      window.alert('PINを変更するには、一度ログアウトしてから再度ログインしてください。');
      return;
    }
    setPinChangeMessage(null);
    setShowPinChangeDialog(true);
  };

  const canNavigateBackFromBottom =
    showPatientFormPanel ||
    showStaffFormPanel ||
    showSchedulePatientListPanel ||
    showDetailPanel ||
    showSchedulePanel ||
    showSettingsDatePanel ||
    showAllPatientListPanel ||
    showStaffListPanel ||
    showSpeechDictionaryPanel ||
    (showDatePanel && (currentUser?.role === 'admin' || currentUser?.role === 'developer'));

  const handleBackClick = () => {
    if (showPatientFormPanel) {
      navigateBackToPatientList();
      return;
    }
    if (showStaffFormPanel) {
      navigateBackToStaffList();
      return;
    }
    if (showSchedulePatientListPanel || showDetailPanel) {
      navigateBackToSchedulePanel();
      return;
    }
    if (showSchedulePanel) {
      if (schedulePanelSource === 'settings') {
        setIsNavigatingBack(true);
        setShowSchedulePatientListPanel(false);
        setShowSchedulePanel(false);
        setShowSettingsDatePanel(true);
      } else {
        navigateBackToDatePanel();
      }
      return;
    }
    if (showSettingsDatePanel) {
      navigateBackToStaffList();
      return;
    }
    if (showAllPatientListPanel || showStaffListPanel) {
      setIsNavigatingBack(true);
      setShowAllPatientListPanel(false);
      setShowStaffListPanel(false);
      setShowPatientFormPanel(false);
      setShowStaffFormPanel(false);
      setShowSettingsPanel(true);
      return;
    }
    if (showSpeechDictionaryPanel) {
      setIsNavigatingBack(true);
      setShowSpeechDictionaryPanel(false);
      return;
    }
    if (showDatePanel && (currentUser?.role === 'admin' || currentUser?.role === 'developer')) {
      navigateBackToWorkflowStaffList();
    }
  };

  return (
    <nav
      className={`relative w-full min-h-[var(--bottom-menu-height)] flex items-center justify-evenly gap-0 px-1 border-t bg-teal-50 z-[60] ${
        useFixedPosition ? 'fixed bottom-0 left-0 right-0' : 'flex-shrink-0'
      }`}
      style={{
        paddingTop: 'var(--bottom-menu-padding-top, 0px)',
        paddingBottom: 'var(--safe-area-bottom)',
      }}
    >
      {pinChangeMessage && (
        <div
          className={`absolute top-2 left-1/2 -translate-x-1/2 text-xs px-3 py-1.5 rounded pointer-events-none ${
            pinChangeMessage.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-300'
              : 'bg-red-100 text-red-800 border border-red-300'
          }`}
        >
          {pinChangeMessage.text}
        </div>
      )}
      <button
        onClick={handleBackClick}
        disabled={!canNavigateBackFromBottom}
        className={`p-2.5 rounded-lg transition-colors ${
          canNavigateBackFromBottom
            ? 'text-gray-600 hover:bg-teal-100'
            : 'text-gray-300 cursor-default'
        }`}
        aria-label="戻る"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={handleWorkflowClick}
        className={`p-2.5 rounded-lg transition-colors ${
          showWorkflowStaffListPanel ||
          showDatePanel ||
          showDetailPanel ||
          (showSchedulePanel && schedulePanelSource === 'workflow') ||
          (showSchedulePatientListPanel && schedulePanelSource === 'workflow')
            ? 'text-white'
            : 'text-gray-600 hover:bg-teal-100'
        }`}
        style={
          showWorkflowStaffListPanel ||
          showDatePanel ||
          showDetailPanel ||
          (showSchedulePanel && schedulePanelSource === 'workflow') ||
          (showSchedulePatientListPanel && schedulePanelSource === 'workflow')
            ? { backgroundColor: '#00a48d' }
            : {}
        }
        aria-label="診療フロー"
      >
        <Calendar size={24} />
      </button>
      {/* 設定ボタン: admin/developer のみ表示（通常スタッフは設定メニューを扱わない） */}
      {(currentUser?.role === 'admin' || currentUser?.role === 'developer') && (
        <button
          onClick={handleSettingsClick}
          className={`p-2.5 rounded-lg transition-colors ${
            showSettingsPanel ||
            showStaffListPanel ||
            showAllPatientListPanel ||
            showPatientFormPanel ||
            showStaffFormPanel ||
            showSettingsDatePanel ||
            (showSchedulePanel && schedulePanelSource === 'settings') ||
            (showSchedulePatientListPanel && schedulePanelSource === 'settings')
              ? 'text-white'
              : 'text-gray-600 hover:bg-teal-100'
          }`}
          style={
            showSettingsPanel ||
            showStaffListPanel ||
            showAllPatientListPanel ||
            showPatientFormPanel ||
            showStaffFormPanel ||
            showSettingsDatePanel ||
            (showSchedulePanel && schedulePanelSource === 'settings') ||
            (showSchedulePatientListPanel && schedulePanelSource === 'settings')
              ? { backgroundColor: '#00a48d' }
              : {}
          }
          aria-label="設定"
        >
          <Settings size={24} />
        </button>
      )}
      <button
        onClick={handleDictionaryClick}
        className={`p-2.5 rounded-lg transition-colors ${
          showSpeechDictionaryPanel ? 'text-white' : 'text-gray-600 hover:bg-teal-100'
        }`}
        style={showSpeechDictionaryPanel ? { backgroundColor: '#00a48d' } : {}}
        aria-label="音声用語辞書"
      >
        <BookOpen size={24} />
      </button>
      <button
        onClick={handleChangePin}
        className="p-2.5 rounded-lg text-gray-600 hover:bg-teal-100 transition-colors"
        aria-label="PINを変更"
      >
        <KeyRound size={24} />
      </button>
      <button
        onClick={handleLogout}
        className="p-2.5 rounded-lg text-gray-600 hover:bg-teal-100 transition-colors"
        aria-label="ログアウト"
      >
        <LogOut size={24} />
      </button>
    </nav>
  );
};
